/**
 * InfinitSales - Auth Service
 * Authentication related API calls
 * 
 * UPDATED: 
 * - Removed manual infinitagents_api_key from registration (now automatic)
 * - Added InfinitAgents integration status and retry methods
 * - Compatible with onboarding flow
 */
import api from './api';
import requestCache from './requestCache';
import { getFeatureFlag } from '../../hooks/useFeatureFlags';
import { clearInboxStore } from '../inbox/inboxStore';
import { clearChatCache } from '../inbox/chatCache';
import { resetInboxPrefetch } from '../inbox/useInboxPrefetch';

const authService = {
  /**
   * Register a new user
   * 
   * InfinitAgents integration is now automatic:
   * - Backend creates InfinitAgents account using user's email
   * - Backend generates API key named "InfinitAgents-WhatsApp"
   * - If integration fails, it can be retried via retryInfinitAgentsIntegration()
   * 
   * Note: After registration, user needs to complete onboarding
   * 
   * @param {string} email - User's email
   * @param {string} password - User's password  
   * @param {string} name - User's display name (optional)
   * @returns {Promise<Object>} Response with user data and token
   */
  async register(email, password, name = null) {
    // NOTE: infinitagents_api_key is no longer sent
    // It's automatically generated by the backend
    const response = await api.post('/api/users/register', {
      email,
      password,
      name,
    });
    return response.data;
  },

  /**
   * Login user
   * Response includes onboarding_completed, default_channel, and infinitagents_status
   */
  async login(email, password) {
    const response = await api.post('/api/users/login', {
      email,
      password,
    });
    return response.data;
  },

  /**
   * Get current user profile
   * Includes onboarding_completed, default_channel, and infinitagents_status
   */
  async getProfile() {
    const response = await api.get('/api/users/me');
    return response.data;
  },

  /**
   * Get stored token
   */
  getToken() {
    return localStorage.getItem('token');
  },

  /**
   * Update user profile
   */
  async updateProfile(data) {
    const response = await api.put('/api/users/me', data);
    return response.data;
  },

  /**
   * Mark a one-time warning as dismissed for the current user.
   * Persiste cross-device em `user.dismissed_warnings[key] = true`.
   * @param {string} key Identificador do aviso (ex.: "external_agent_overwrite").
   */
  async dismissWarning(key) {
    const response = await api.post('/api/users/me/dismiss-warning', { key });
    return response.data;
  },

  /**
   * Get user labels
   * Catálogo: cache 60s + dedup em voo — LabelPickerModal/ImportPage/
   * ConfigDrawer refaziam este GET a cada abertura; as mutações abaixo
   * invalidam na hora.
   */
  async getLabels({ force = false } = {}) {
    return requestCache.cachedGet(
      'labels:catalog',
      async () => {
        const response = await api.get('/api/users/me/labels');
        return response.data;
      },
      { ttl: 60_000, force },
    );
  },

  /**
   * Create label (coluna). Funis: `pipelineId` define em qual funil a coluna
   * entra (default se omitido).
   */
  async createLabel(name, color, pipelineId) {
    const body = { name, color };
    if (pipelineId) body.pipeline_id = pipelineId;
    const response = await api.post('/api/users/me/labels', body);
    requestCache.invalidate('labels:');
    requestCache.invalidate('funnels:');
    return response.data;
  },

  // ---- Funis (grupos de etiquetas / colunas) ----
  // Catálogo de funis: 7+ call sites refaziam GET /api/pipelines a cada
  // mount/modal/evento SSE. Cache 5min compartilhado (o hook
  // useContactLabelsCount usa a MESMA chave via force p/ auto-cura).
  async listFunnels({ force = false } = {}) {
    return requestCache.cachedGet(
      'funnels:catalog',
      async () => {
        const response = await api.get('/api/pipelines');
        return response.data?.data || [];
      },
      { ttl: 5 * 60_000, force },
    );
  },
  async createFunnel(name) {
    const response = await api.post('/api/pipelines', { name });
    requestCache.invalidate('funnels:');
    return response.data?.data;
  },
  async updateFunnel(funnelId, name) {
    const response = await api.patch(`/api/pipelines/${funnelId}`, { name });
    requestCache.invalidate('funnels:');
    return response.data?.data;
  },

  // Torna o funil o padrão do workspace; o backend desmarca os demais.
  async setDefaultFunnel(funnelId) {
    const response = await api.patch(`/api/pipelines/${funnelId}`, { is_default: true });
    requestCache.invalidate('funnels:');
    return response.data?.data;
  },
  async deleteFunnel(funnelId) {
    const response = await api.delete(`/api/pipelines/${funnelId}`);
    requestCache.invalidate('funnels:');
    requestCache.invalidate('labels:');
    return response.data?.data;
  },
  async reorderLabels(funnelId, labelIds) {
    const response = await api.put(`/api/pipelines/${funnelId}/labels/order`, { label_ids: labelIds });
    requestCache.invalidate('funnels:');
    requestCache.invalidate('labels:');
    return response.data?.data;
  },

  // Automação de entrada de um funil (drawer unificado): o que disparar +
  // distribuir + regras + tags. No funil padrão, a distribuição é a roleta de
  // novos leads (espelhada em user.entry_automation).
  async getFunnelEntryAutomation(funnelId) {
    const response = await api.get(`/api/pipelines/${funnelId}/entry-automation`);
    return response.data;
  },
  async setFunnelEntryAutomation(funnelId, data) {
    const response = await api.put(`/api/pipelines/${funnelId}/entry-automation`, data);
    return response.data;
  },

  /**
   * Update label (name and/or color)
   */
  async updateLabel(labelId, data) {
    const response = await api.put(`/api/users/me/labels/${labelId}`, data);
    requestCache.invalidate('labels:');
    requestCache.invalidate('funnels:');
    return response.data;
  },

  /**
   * Delete label
   */
  async deleteLabel(labelId) {
    const response = await api.delete(`/api/users/me/labels/${labelId}`);
    requestCache.invalidate('labels:');
    requestCache.invalidate('funnels:');
    return response.data;
  },

  /**
   * Attach an existing label to a contact (idempotent, fans out across channels).
   */
  async assignLabelToContact(phone, labelId, ownerUserId, outcomeReason) {
    const response = await api.post(
      `/api/users/me/contacts/${encodeURIComponent(phone)}/labels`,
      // ownerUserId: move contato de OUTRO workspace (Kanban company-wide); o
      // servidor checa se o caller (admin/membro com acesso) pode.
      // outcomeReason: motivo do fechamento quando a etapa de destino é de
      // ganho/perda. Ignorado pelo servidor nas demais etapas.
      {
        label_id: labelId,
        ...(ownerUserId ? { owner_user_id: ownerUserId } : {}),
        ...(outcomeReason ? { outcome_reason: outcomeReason } : {}),
      }
    );
    requestCache.invalidate(`contact-labels:${phone}`);
    return response.data;
  },

  /**
   * Detach a label from a contact across all channels.
   */
  async removeLabelFromContact(phone, labelId, ownerUserId) {
    const response = await api.delete(
      `/api/users/me/contacts/${encodeURIComponent(phone)}/labels/${labelId}`,
      ownerUserId ? { params: { owner_user_id: ownerUserId } } : undefined
    );
    requestCache.invalidate(`contact-labels:${phone}`);
    return response.data;
  },

  /**
   * Get the labels attached to a single contact.
   *
   * Frente C4 (flag `chat_cache`): abrir uma conversa disparava este GET mais
   * de uma vez (chip do header, LabelPickerModal, drawer). Com a flag, a
   * leitura passa pelo requestCache com TTL curto e dedup em voo: UMA
   * requisicao por abertura. As mutacoes de etiqueta do contato e o SSE
   * CONTACT_LABELS_CHANGED invalidam a chave (ver useContactLabelsCount).
   */
  async getContactLabels(phone) {
    const fetcher = async () => {
      const response = await api.get(
        `/api/users/me/contacts/${encodeURIComponent(phone)}/labels`
      );
      return response.data;
    };
    if (!getFeatureFlag('chat_cache')) return fetcher();
    return requestCache.cachedGet(`contact-labels:${phone}`, fetcher, { ttl: 15_000 });
  },

  /** Frente C4: derruba o cache de etiquetas de um contato (ou de todos). */
  invalidateContactLabels(phone) {
    requestCache.invalidate(phone ? `contact-labels:${phone}` : 'contact-labels:');
  },

  /**
   * Build the kanban payload: { labels, buckets: { <label_id|"unlabeled">: [...] } }
   * Só dedup em voo (ttl 0): é a request mais PESADA do inbox (workspace
   * inteiro) e o ChatList remonta a cada conversa aberta no mobile — chamadas
   * simultâneas colapsam na mesma promise, sem servir dado velho.
   */
  /**
   * Etiquetas de um LOTE de telefones — {telefone: [label_ids]}.
   *
   * O chip de funil da lista de chats usava o getContactsByLabel, que devolve o
   * Kanban INTEIRO (18,3s e 13 MB no maior workspace) para pintar ~30 linhas.
   * Aqui vai só o que está na tela. Sem cache: o mapa muda com a paginação e a
   * busca, e o chamador já faz debounce.
   */
  async getContactsLabelsBatch(phones) {
    const lista = (phones || []).filter(Boolean);
    if (!lista.length) return {};
    // Bloco A3 (inbox): o backend corta cada chamada em 200 chaves sem avisar,
    // entao a lista acumulada perdia o chip dos telefones alem dos 200
    // primeiros em ordem alfabetica (depois de ~7 paginas). Com a flag, vai em
    // lotes de 200, dois por vez, e os resultados sao juntados.
    const LOTE = 200;
    if (getFeatureFlag('inbox_labels_batch_chunked') && lista.length > LOTE) {
      const lotes = [];
      for (let i = 0; i < lista.length; i += LOTE) lotes.push(lista.slice(i, i + LOTE));
      const juntos = {};
      for (let i = 0; i < lotes.length; i += 2) {
        const partes = await Promise.all(lotes.slice(i, i + 2).map((lote) => api
          .get('/api/users/me/contacts-labels', { params: { phones: lote.join(',') } })
          .then((r) => r.data?.data || {})));
        partes.forEach((p) => Object.assign(juntos, p));
      }
      return juntos;
    }
    const response = await api.get('/api/users/me/contacts-labels', {
      params: { phones: lista.join(',') },
    });
    return response.data?.data || {};
  },

  async getContactsByLabel(pipelineId) {
    const key = `contactsByLabel:${pipelineId || 'all'}`;
    return requestCache.cachedGet(
      key,
      async () => {
        const url = pipelineId
          ? `/api/users/me/contacts-by-label?pipeline_id=${encodeURIComponent(pipelineId)}`
          : '/api/users/me/contacts-by-label';
        const response = await api.get(url);
        return response.data;
      },
      { ttl: 0 },
    );
  },

  // Kanban PAGINADO: 1ª página por coluna + has_more.
  // `deltaSince` (flag kanban_delta, ISO UTC): pede só as colunas tocadas
  // desde o carimbo; o servidor devolve `delta: true` com essas colunas, ou o
  // board inteiro com `delta: false` quando não sabe o que mudou. Sem o
  // parâmetro a chamada é a de sempre.
  async getKanbanBoard(pipelineId, { deltaSince = null } = {}) {
    const params = new URLSearchParams();
    if (pipelineId) params.set('pipeline_id', pipelineId);
    if (deltaSince) params.set('delta_since', deltaSince);
    const qs = params.toString();
    const url = qs ? `/api/users/me/kanban-board?${qs}` : '/api/users/me/kanban-board';
    const response = await api.get(url);
    return response.data;
  },

  // Próxima página de UMA coluna (Carregar mais). `q` filtra a etapa por
  // nome/telefone no server-side (busca por lead DENTRO da etapa).
  // `cursor` (flag kanban_cursor_paging): `ts` do ÚLTIMO card carregado; o
  // servidor devolve os anteriores a ele. O `offset` continua indo junto: um
  // backend sem o cursor pagina por offset como hoje.
  async getKanbanLabelPage(bucket, pipelineId, offset = 0, limit = 30, q = null, createdFrom = null, createdTo = null, { cursor = null } = {}) {
    const params = new URLSearchParams();
    params.set('bucket', bucket);
    if (pipelineId) params.set('pipeline_id', pipelineId);
    params.set('offset', String(offset));
    params.set('limit', String(limit));
    if (cursor !== null && cursor !== undefined) params.set('cursor', String(cursor));
    if (q && q.trim().length >= 2) params.set('q', q.trim());
    if (createdFrom) params.set('created_from', createdFrom);
    if (createdTo) params.set('created_to', createdTo);
    const response = await api.get(`/api/users/me/kanban-board/contacts?${params.toString()}`);
    return response.data;
  },

  // #3: busca server-side de leads no kanban (varre o banco, não só a página).
  // Exporta os contatos do funil em CSV/XLSX. O backend aplica o MESMO
  // escopo/visibilidade do board (4 canais) e o filtro por campo vai no `cf`.
  // responseType blob: o corpo é o arquivo; o nome vem do Content-Disposition.
  // `createdFrom` (ISO) restringe aos contatos criados a partir da data: e o
  // que faz o arquivo falar da MESMA populacao que um painel de janela. Sem
  // ele, clicar numa origem que o painel diz ter 44 leads baixaria o acervo
  // inteiro daquela origem e o cliente ficaria com dois numeros para a mesma
  // pergunta.
  async exportKanbanContacts({ pipelineId, bucket = null, q = null, detail = 'basico', format = 'csv', customFields = null, createdFrom = null, createdTo = null }) {
    const params = new URLSearchParams();
    if (pipelineId) params.set('pipeline_id', pipelineId);
    if (bucket) params.set('bucket', bucket);
    if (q) params.set('q', q);
    if (createdFrom) params.set('created_from', createdFrom);
    if (createdTo) params.set('created_to', createdTo);
    params.set('detail', detail);
    params.set('format', format);
    (customFields || []).forEach(({ field_id, value }) => {
      if (field_id && value) params.append('cf', `${field_id}:${value}`);
    });
    const response = await api.get(
      `/api/users/me/kanban-board/export?${params.toString()}`,
      { responseType: 'blob' },
    );
    const cd = response.headers?.['content-disposition'] || '';
    const m = cd.match(/filename="?([^";]+)"?/i);
    const rows = parseInt(response.headers?.['x-export-rows'], 10);
    return {
      blob: response.data,
      filename: (m && m[1]) || `contatos.${format === 'xlsx' ? 'xlsx' : 'csv'}`,
      rows: Number.isNaN(rows) ? null : rows,
    };
  },

  async searchKanban(query, pipelineId, customFields = null, createdFrom = null, createdTo = null) {
    const params = new URLSearchParams();
    params.set('q', query || '');
    if (pipelineId) params.set('pipeline_id', pipelineId);
    // Filtro por campo personalizado: ?cf=campo:valor (repetível). Mesmo
    // formato de fio dos 4 inboxes — o backend usa o mesmo motor core.
    (customFields || []).forEach(({ field_id, value }) => {
      if (field_id && value) params.append('cf', `${field_id}:${value}`);
    });
    // "Entrou no CRM entre": mesmo corte do export (created_from/created_to).
    if (createdFrom) params.set('created_from', createdFrom);
    if (createdTo) params.set('created_to', createdTo);
    const response = await api.get(`/api/users/me/kanban-board/search?${params.toString()}`);
    return response.data;
  },

  // ============================================
  // 🆕 INFINITAGENTS INTEGRATION
  // ============================================

  /**
   * Get InfinitAgents integration status.
   * 
   * Returns:
   * - status: "success" | "pending" | "failed" | "not_attempted"
   * - has_api_key: boolean
   * - error: string | null
   * - needs_retry: boolean
   * 
   * @returns {Promise<Object>} Integration status
   */
  async getInfinitAgentsStatus() {
    const response = await api.get('/api/users/me/infinitagents/status');
    return response.data;
  },

  /**
   * Retry InfinitAgents integration.
   * 
   * Call this when:
   * - User accesses a feature requiring InfinitAgents
   * - infinitagents_status is "pending" or "failed"
   * 
   * @returns {Promise<Object>} Retry result with updated user data
   */
  async retryInfinitAgentsIntegration() {
    const response = await api.post('/api/users/me/infinitagents/retry');
    return response.data;
  },

  /**
   * Check if InfinitAgents integration needs retry and perform it.
   * 
   * This is a convenience method that checks status and retries if needed.
   * Returns true if integration is successful (either already or after retry).
   * 
   * @returns {Promise<boolean>} True if integration is successful
   */
  async ensureInfinitAgentsIntegration() {
    try {
      const statusResponse = await this.getInfinitAgentsStatus();
      const status = statusResponse.data;

      // Already successful
      if (status.status === 'success' && status.has_api_key) {
        return true;
      }

      // Needs retry
      if (status.needs_retry) {
        const retryResponse = await this.retryInfinitAgentsIntegration();
        return retryResponse.data?.infinitagents_status === 'success';
      }

      return false;
    } catch (error) {
      console.error('Error ensuring InfinitAgents integration:', error);
      return false;
    }
  },

  /**
   * Get user's agents from InfinitAgents
   * Requires infinitagents_api_key to be configured
   */
  async getAgents() {
    const response = await api.get('/agents');
    return response.data;
  },

  /**
   * Set active agent for autopilot
   * @param {string|null} agentId - Agent ID or null to disable
   */
  async setActiveAgent(agentId) {
    const response = await api.put('/api/users/me/active-agent', {
      agent_id: agentId,
    });
    return response.data;
  },

  // ============================================
  // 🆕 AUTOPILOT CONFIGURATION
  // ============================================

  /**
   * Get autopilot configuration.
   * Returns autopilot_mode, autopilot_contacts, active_agent_id
   */
  async getAutopilotConfig() {
    const response = await api.get('/api/users/me/autopilot-config');
    return response.data;
  },

  /**
   * Update autopilot configuration.
   * @param {string} mode - "all" | "specific" | "scheduled" | "off"
   * @param {string[]} contacts - Phone numbers for "specific" mode
   * @param {object} schedule - Schedule config for "scheduled" mode { enabled, slots: [{ days, start_time, end_time }] }
   */
  async updateAutopilotConfig(mode, contacts = null, schedule = null) {
    const body = {
      autopilot_mode: mode,
      autopilot_contacts: contacts,
    };
    if (schedule) body.autopilot_schedule = schedule;
    const response = await api.put('/api/users/me/autopilot-config', body);
    return response.data;
  },

  /**
   * Define a janela de buffer/debounce (segundos) antes da resposta do agente.
   * Acumula mensagens picadas do mesmo contato numa janela e responde uma vez.
   * 0 = desligado (responde imediatamente).
   * @param {number} seconds - 0 a 120
   */
  async updateAgentBuffer(seconds) {
    const response = await api.put('/api/users/me/agent-buffer', {
      agent_buffer_seconds: Number(seconds) || 0,
    });
    return response.data;
  },

  /**
   * Toggle autopilot for a specific contact.
   * @param {string} phone - Phone number
   * @param {boolean} enabled - True to add, false to remove
   */
  async toggleAutopilotContact(phone, enabled) {
    const response = await api.put('/api/users/me/autopilot-contact', {
      phone,
      enabled,
    });
    return response.data;
  },

  /**
   * Toggle blocked contact (exclude from auto-respond when mode is "all").
   * @param {string} phone - Phone number
   * @param {boolean} blocked - True to block, false to unblock
   */
  async toggleBlockedContact(phone, blocked) {
    const response = await api.put('/api/users/me/blocked-contact', {
      phone,
      blocked,
    });
    return response.data;
  },

  // ============================================
  // 🆕 ONBOARDING
  // ============================================

  /**
   * Get onboarding status.
   * 
   * Returns:
   * - onboarding_completed: boolean
   * - default_channel: string | null
   * - infinitagents_status: string
   * - has_api_key: boolean
   * - redirect: string (suggested redirect path)
   * 
   * @returns {Promise<Object>} Onboarding status
   */
  async getOnboardingStatus() {
    const response = await api.get('/api/users/me/onboarding-status');
    return response.data;
  },

  /**
   * Select channel during onboarding.
   * @param {string} channel - 'wa_personal' or 'wa_business'
   * @returns {Promise<Object>} Response with redirect path
   */
  async selectChannel(channel) {
    const response = await api.post('/api/users/me/select-channel', { channel });
    return response.data;
  },

  // ============================================
  // SESSION MANAGEMENT
  // ============================================

  /**
   * Logout - clear local storage
   */
  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Frentes C2 e C4: lista de conversas por canal e cache de conversas
    // abertas nao sobrevivem a troca de conta na mesma aba.
    clearInboxStore();
    clearChatCache();
    resetInboxPrefetch();
    // Encerra qualquer sessão delegada pendente: sem isso o marcador
    // sobrevivia ao logout e o próximo login (mesma aba) herdava um estado
    // de "workspace de outra pessoa" inconsistente.
    localStorage.removeItem('infinitsales-delegation');
    // Limpa os guards de auto-entrada (sessionStorage) para que, ao relogar,
    // a preferência "entrar sempre neste workspace" volte a funcionar — o
    // guard só existe para evitar loop DENTRO de uma sessão de página.
    try {
      Object.keys(sessionStorage)
        .filter((k) => k.startsWith('autoworkspace-tried-'))
        .forEach((k) => sessionStorage.removeItem(k));
    } catch {}
    // NÃO removemos `infinitsales-autoworkspace-*`: é a preferência persistente
    // de auto-entrada, deve sobreviver ao logout.
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!localStorage.getItem('token');
  },

  /**
   * Get stored user
   */
  getStoredUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  /**
   * Store auth data
   */
  storeAuthData(user, token) {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
  },

  /**
   * Update stored user data
   * @param {Object} userData - Updated user data
   */
  updateStoredUser(userData) {
    const currentUser = this.getStoredUser();
    const updatedUser = { ...currentUser, ...userData };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    return updatedUser;
  },

  /**
   * Conexões por onde um lead pode CHEGAR (um item por membro da empresa).
   * Usada pela origem por número de entrada, na automação de entrada do funil:
   * a regra é chaveada pelo nome interno da instância, que a tela não precisa
   * conhecer.
   */
  async listEntryConnections() {
    const response = await api.get('/api/users/me/entry-connections');
    return response.data?.data || [];
  },
};

export default authService;